import {
  DevHubChip,
  DevHubColorProps,
  DevHubGrid,
  DevHubLink,
  devhubMakeStyles,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { format } from 'date-fns';
import { ReactNode } from 'react';

export type WorkboxItemProps = {
  chipLabel?: string;
  chipColor?: DevHubColorProps;
  link?: string;
  title: string;
  date?: Date | string;
  dueDate?: string;
  body?: ReactNode | string;
  includeBorder?: boolean;
  badgeNumber?: number;
  id?: string;
};

/**
 * Custom WorkboxItem styles
 * @param theme - the DevHub theme
 * @constructor
 */
export const universalWorklistStyles = devhubMakeStyles((theme: any) => ({
  workListItem: {
    borderColor: theme.palette.devhubBackground.workbox,
    borderLeft: `2px solid ${theme.palette.devhubBackground.workbox}`,
    borderRight: `2px solid ${theme.palette.devhubBackground.workbox}`,
    borderBottom: `2px solid ${theme.palette.devhubBackground.workbox}`,
    width: 'calc(100%)',
    padding: '0.25rem',
    marginTop: '-5px',
  },
  badgeBox: {
    // align badge off the left edge of the parent container with an absolute position to overlap
    position: 'absolute',
    left: '-10px',
  },
  badge: {
    backgroundColor: theme.palette.devhubBackground.workbox,
    color: theme.palette.text.primary,
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '-1.3rem',
    marginBottom: '-2rem',
  },
}));

/**
 * Custom WorkboxItem component
 * @param props WorkboxItemProps
 * @constructor
 */
export const WorkBoxItem = (props: WorkboxItemProps) => {
  const classes = universalWorklistStyles();
  return (
    <DevHubGrid lrPadding="small" maximizeWidth>
      {props?.badgeNumber && (
        <div className={classes.badge}>
          <DevHubTypography variant="body2" weight="bold">
            {props.badgeNumber}
          </DevHubTypography>
        </div>
      )}
      <DevHubGrid
        container
        alignItems="center"
        justifyContent="space-between"
        backgroundColor="workbox"
      >
        <DevHubGrid item xs style={{ overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            {props?.chipLabel && (
              <div style={{ flexShrink: 0 }}>
                <DevHubTooltip title="Status">
                  <DevHubChip
                    label={props?.chipLabel}
                    noBottomMargin
                    chipSize="small"
                    chipColor={props.chipColor}
                  />
                </DevHubTooltip>
              </div>
            )}
            <div style={{ overflow: 'hidden', marginLeft: '4px' }}>
              {props?.link ? (
                <DevHubLink
                  href={`${props.link}`}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  display="inline"
                  id={`${props.id}`}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  {props.title}
                </DevHubLink>
              ) : (
                <DevHubTypography
                  display="inline"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  {props.title}
                </DevHubTypography>
              )}
            </div>
          </div>
        </DevHubGrid>

        {props?.date && (
          <DevHubGrid item style={{ whiteSpace: 'nowrap' }}>
            {format(new Date(props?.date), 'MM/dd/yyyy')}
          </DevHubGrid>
        )}
        {!props?.date && props?.dueDate && (
          <DevHubGrid item style={{ whiteSpace: 'nowrap' }}>
            <DevHubTypography display="inline">Due </DevHubTypography>
            {format(new Date(props?.dueDate), 'MM/dd/yyyy')}
          </DevHubGrid>
        )}
      </DevHubGrid>
      <DevHubGrid
        container
        tbPadding="medium"
        alignItems="center"
        justifyContent="space-between"
      >
        <div className={classes.workListItem}>
          {typeof props?.body === 'string' ? (
            <DevHubTypography variant="body2" maximumHeight="large" noOverflow>
              {props?.body.length > 40
                ? `${props.body.slice(0, 40)}...`
                : props.body}
            </DevHubTypography>
          ) : (
            props?.body
          )}
        </div>
      </DevHubGrid>
    </DevHubGrid>
  );
};
